const HUE_BUCKET_SIZE = 30;
const HUE_BUCKET_COUNT = 12;
const HUE_TONES = [
    'RED',
    'ORANGE',
    'YELLOW',
    'YELLOW_GREEN',
    'GREEN',
    'CYAN',
    'CYAN_BLUE',
    'BLUE',
    'BLUE_PURPLE',
    'PURPLE',
    'MAGENTA',
    'RED_PURPLE'
];
const WARM_TONES = new Set(['RED', 'ORANGE', 'YELLOW', 'RED_PURPLE']);
const COOL_TONES = new Set(['CYAN', 'CYAN_BLUE', 'BLUE', 'BLUE_PURPLE']);
const NEUTRAL_TONES = new Set(['YELLOW_GREEN', 'GREEN', 'PURPLE', 'MAGENTA']);

export class HslClassifier {
    static classifyLightness(mean, range, skewness, kurtosis) {
        return {
            level: this.classifyLightnessLevel(mean),
            dynamicRange: this.classifyLightnessRange(range),
            skewness: this.classifyLightnessSkewness(skewness),
            kurtosis: this.classifyLightnessKurtosis(kurtosis)
        };
    }

    static classifyLightnessLevel(mean) {
        if (mean == null) return null;
        if (mean > 56) return 'BRIGHT';
        if (mean < 44) return 'DARK';
        return 'NEUTRAL';
    }

    static classifyLightnessRange(range) {
        if (range == null) return null;
        if (range >= 94) return 'COMPLETE';
        if (range >= 71) return 'PARTIAL';
        return 'LIMITED';
    }

    static classifyLightnessSkewness(skewness) {
        if (skewness == null) return null;
        if (skewness > 0.5) return 'DARK_BIASED';
        if (skewness < -0.5) return 'LIGHT_BIASED';
        return 'BALANCED';
    }

    static classifyLightnessKurtosis(kurtosis) {
        if (kurtosis == null) return null;
        if (kurtosis > 0.5) return 'CONCENTRATED';
        if (kurtosis < -0.5) return 'GENTLE';
        return 'FLAT';
    }

    static classifySaturation(mean, iqr, std, mode, median, skewness) {
        return {
            level: this.classifySaturationLevel(mean),
            distribution: this.classifySaturationDistribution(iqr),
            stability: this.classifySaturationStability(mean, mode, median, skewness),
            deviation: this.classifySaturationDeviation(std)
        };
    }

    static classifySaturationLevel(mean) {
        if (mean == null) return null;
        if (mean >= 60) return 'STRONG';
        if (mean >= 30) return 'MODERATE';
        return 'WEAK';
    }

    static classifySaturationDistribution(iqr) {
        if (iqr == null) return null;
        if (iqr <= 20) return 'CONCENTRATED';
        if (iqr <= 40) return 'SCATTERED';
        return 'VERY_SCATTERED';
    }

    static classifySaturationStability(mean, mode, median, skewness) {
        if (mean == null || mode == null || median == null || skewness == null) return null;
        if (Math.abs(mode - mean) < 5 && Math.abs(mode - median) < 5) return 'stable';
        if (mean > mode && skewness > 0) return 'highSaturationHighlights';
        if (mean < mode && skewness < 0) return 'lowSaturationShadows';
        return 'diverse';
    }

    static classifySaturationDeviation(std) {
        if (std == null) return null;
        if (std < 12) return 'DEVIATION_WEAK';
        if (std < 25) return 'DEVIATION_MODERATE';
        return 'DEVIATION_STRONG';
    }

    static classifyHue(stats, distribution, saturationStats) {
        if (!stats) {
            return {
                dominantHues: [],
                tone: null,
                temperature: null,
                temperatureRatios: {
                    warm: 0,
                    cool: 0,
                    neutral: 0
                },
                distribution: null,
                stability: null,
                isAchromatic: false
            };
        }

        const isAchromatic = this.isAchromatic(distribution, saturationStats);
        const hueBuckets = this.resolveHueBuckets(stats, distribution?.h);
        const dominantHues = isAchromatic ? [] : this.selectDominantHues(hueBuckets);
        const tone = isAchromatic
            ? 'ACHROMATIC'
            : (dominantHues[0]?.tone || this.classifyHueTone(stats, distribution?.h));
        const temperatureData = this.classifyHueTemperature(dominantHues, isAchromatic);

        return {
            dominantHues,
            tone,
            temperature: temperatureData.temperature,
            temperatureRatios: temperatureData.temperatureRatios,
            distribution: this.classifyHueDistribution(distribution?.h, stats?.iqr),
            stability: this.classifyHueStability(stats, distribution?.h),
            isAchromatic
        };
    }

    static isAchromatic(distribution, saturationStats) {
        if (distribution?.s?.length > 0) {
            const lowSaturationRatio = distribution.s
                .slice(0, 9)
                .reduce((sum, value) => sum + value, 0);
            return lowSaturationRatio >= 60;
        }

        if (!saturationStats) return false;
        return saturationStats.mean < 10 && saturationStats.iqr < 8;
    }

    static classifyHueTone(stats, hueDistribution) {
        const bucketIndex = this.resolveHueBucket(stats, hueDistribution);
        if (bucketIndex == null) return null;
        return HUE_TONES[bucketIndex] || null;
    }

    static resolveHueBuckets(stats, hueDistribution) {
        if (Array.isArray(hueDistribution) && hueDistribution.length > 0) {
            return this.aggregateHueBuckets(hueDistribution);
        }

        const fallbackIndex = this.resolveHueBucket(stats, hueDistribution);
        if (fallbackIndex == null) {
            return new Array(HUE_BUCKET_COUNT).fill(0);
        }

        const buckets = new Array(HUE_BUCKET_COUNT).fill(0);
        buckets[fallbackIndex] = 1;
        return buckets;
    }

    static selectDominantHues(hueBuckets, options = {}) {
        const defaults = this.getDominantHueOptions();
        const {
            significantThreshold,
            minCoverage,
            maxCount
        } = { ...defaults, ...options };

        if (!Array.isArray(hueBuckets) || hueBuckets.length === 0) {
            return [];
        }

        const total = hueBuckets.reduce((sum, value) => sum + Math.max(0, Number(value) || 0), 0);
        if (total <= 0) return [];

        const buckets = hueBuckets.map((value, index) => ({
            index,
            ratio: Math.max(0, Number(value) || 0) / total
        }));
        const sorted = [...buckets].sort((a, b) => b.ratio - a.ratio);

        const significant = sorted
            .filter((bucket) => bucket.ratio >= significantThreshold)
            .slice(0, maxCount);

        const selected = significant.length > 0 ? [...significant] : [sorted[0]];

        let coverage = selected.reduce((sum, bucket) => sum + bucket.ratio, 0);
        if (significant.length > 0 && coverage < minCoverage) {
            sorted.forEach((bucket) => {
                if (coverage >= minCoverage || selected.length >= maxCount) return;
                const exists = selected.some((item) => item.index === bucket.index);
                if (!exists) {
                    selected.push(bucket);
                    coverage += bucket.ratio;
                }
            });
        }

        return selected.map((bucket) => ({
            tone: HUE_TONES[bucket.index] || null,
            ratio: Number(bucket.ratio.toFixed(4))
        })).filter((item) => item.tone);
    }

    static getDominantHueOptions() {
        return {
            significantThreshold: 0.06,
            minCoverage: 0.65,
            maxCount: 3
        };
    }

    static getHueToneByIndex(index) {
        if (index == null) return null;
        return HUE_TONES[index] || null;
    }

    static resolveHueBucket(stats, hueDistribution) {
        if (Array.isArray(hueDistribution) && hueDistribution.length > 0) {
            const buckets = this.aggregateHueBuckets(hueDistribution);
            let maxIndex = 0;
            let maxValue = -Infinity;
            buckets.forEach((value, index) => {
                if (value > maxValue) {
                    maxValue = value;
                    maxIndex = index;
                }
            });
            return maxValue > 0 ? maxIndex : null;
        }

        const fallback = stats?.mode ?? stats?.mean;
        if (fallback == null) return null;
        return this.normalizeHueBucket(fallback);
    }

    static aggregateHueBuckets(hueDistribution) {
        const buckets = new Array(HUE_BUCKET_COUNT).fill(0);
        hueDistribution.forEach((value, index) => {
            const bucketIndex = Math.min(
                HUE_BUCKET_COUNT - 1,
                Math.floor(index / HUE_BUCKET_SIZE)
            );
            buckets[bucketIndex] += value;
        });
        return buckets;
    }

    static normalizeHueBucket(value) {
        const normalized = ((value % 360) + 360) % 360;
        return Math.min(HUE_BUCKET_COUNT - 1, Math.floor(normalized / HUE_BUCKET_SIZE));
    }

    static classifyHueTemperature(dominantHues, isAchromatic = false) {
        if (isAchromatic) {
            return {
                temperature: 'NEUTRAL',
                temperatureRatios: {
                    warm: 0,
                    cool: 0,
                    neutral: 0
                }
            };
        }

        const ratios = {
            warm: 0,
            cool: 0,
            neutral: 0
        };

        (dominantHues || []).forEach(({ tone, ratio }) => {
            if (!tone || !Number.isFinite(ratio)) return;
            if (WARM_TONES.has(tone)) {
                ratios.warm += ratio;
            } else if (COOL_TONES.has(tone)) {
                ratios.cool += ratio;
            } else if (NEUTRAL_TONES.has(tone)) {
                ratios.neutral += ratio;
            }
        });

        const warmCoolDelta = ratios.warm - ratios.cool;
        let temperature = 'NEUTRAL';
        if (warmCoolDelta >= 0.2) {
            temperature = 'WARM';
        } else if (warmCoolDelta <= -0.2) {
            temperature = 'COOL';
        } else if (Math.abs(warmCoolDelta) < 0.2 && Math.max(ratios.warm, ratios.cool) >= 0.35) {
            temperature = 'MIXED';
        }

        return {
            temperature,
            temperatureRatios: {
                warm: Number(ratios.warm.toFixed(4)),
                cool: Number(ratios.cool.toFixed(4)),
                neutral: Number(ratios.neutral.toFixed(4))
            }
        };
    }

    static classifyHueDistribution(hueDistribution, iqr) {
        if (Array.isArray(hueDistribution) && hueDistribution.length > 0) {
            const buckets = this.aggregateHueBuckets(hueDistribution);
            const { entropy, peakCount } = this.computeHueDistributionStats(buckets);

            // 低峰数 + 低熵表示集中；高峰数或高熵表示分散
            if (peakCount <= 2 && entropy <= 2.0) return 'CONCENTRATED';
            if (peakCount <= 4 && entropy <= 2.6) return 'SCATTERED';
            return 'VERY_SCATTERED';
        }

        if (iqr == null) return null;
        if (iqr <= 30) return 'CONCENTRATED';
        if (iqr <= 90) return 'SCATTERED';
        return 'VERY_SCATTERED';
    }

    static classifyHueStability(stats, hueDistribution) {
        if (!stats || stats.mode == null || stats.mean == null || stats.median == null) return null;
        const modeMean = this.circularDistance(stats.mode, stats.mean);
        const modeMedian = this.circularDistance(stats.mode, stats.median);

        if (Array.isArray(hueDistribution) && hueDistribution.length > 0) {
            const buckets = this.aggregateHueBuckets(hueDistribution);
            const { resultantLength, peakCount } = this.computeHueDistributionStats(buckets);

            // 稳定性需要：强集中度（resultantLength高）+ 峰少 + 中心稳定
            // 对 modeMean 提供软阈值，避免在高度集中时因微小偏移导致误判
            if (resultantLength >= 0.6 && peakCount <= 2) {
                if (modeMean <= 12 && modeMedian <= 10) {
                    return 'stable';
                }
                if (resultantLength >= 0.7 && modeMean <= 18 && modeMedian <= 12) {
                    return 'stable';
                }
            }
            return 'shifted';
        }

        return modeMean < 10 && modeMedian < 10 ? 'stable' : 'shifted';
    }

    static computeHueDistributionStats(buckets) {
        const total = buckets.reduce((sum, value) => sum + Math.max(0, Number(value) || 0), 0);
        if (total <= 0) {
            return { entropy: 0, resultantLength: 0, peakCount: 0 };
        }

        const ratios = buckets.map((value) => Math.max(0, Number(value) || 0) / total);
        const entropy = this.computeEntropy(ratios);
        const resultantLength = this.computeResultantLength(ratios);
        const peakCount = this.countPeaks(ratios);

        return { entropy, resultantLength, peakCount };
    }

    static computeEntropy(ratios) {
        // Shannon entropy (base 2); 12 buckets max entropy ~ log2(12) ≈ 3.585
        return ratios.reduce((sum, p) => {
            if (!p) return sum;
            return sum - p * Math.log2(p);
        }, 0);
    }

    static computeResultantLength(ratios) {
        // Circular concentration: R in [0,1], higher means more concentrated
        const bucketAngle = (2 * Math.PI) / HUE_BUCKET_COUNT;
        let x = 0;
        let y = 0;
        ratios.forEach((p, index) => {
            if (!p) return;
            const theta = (index + 0.5) * bucketAngle;
            x += p * Math.cos(theta);
            y += p * Math.sin(theta);
        });
        return Math.sqrt(x * x + y * y);
    }

    static countPeaks(ratios, minRatio = 0.08) {
        // 通过局部最大值计数峰数，避免把噪声当作主峰
        const count = ratios.length;
        if (count === 0) return 0;
        let peaks = 0;
        for (let i = 0; i < count; i += 1) {
            const prev = ratios[(i - 1 + count) % count];
            const curr = ratios[i];
            const next = ratios[(i + 1) % count];
            if (curr >= minRatio && curr >= prev && curr >= next && (curr > prev || curr > next)) {
                peaks += 1;
            }
        }
        return peaks;
    }

    static classifyLocalColor(sStats, lStats) {
        if (!sStats || !lStats) {
            return { highlights: [], lowlights: [], balance: 'neutral' };
        }

        const hasHighlights = sStats.q3 >= sStats.mean + 10 && lStats.q3 >= lStats.mean + 8;
        const hasLowlights = sStats.q1 <= sStats.mean - 10 && lStats.q1 <= lStats.mean - 8;

        const highlights = hasHighlights ? ['highlights'] : [];
        const lowlights = hasLowlights ? ['lowlights'] : [];
        const balance = hasHighlights && hasLowlights
            ? 'both'
            : hasHighlights
                ? 'highlights'
                : hasLowlights
                    ? 'lowlights'
                    : 'neutral';

        return { highlights, lowlights, balance };
    }

    static circularDistance(a, b) {
        if (a == null || b == null) return null;
        const delta = Math.abs(a - b);
        return Math.min(delta, 360 - delta);
    }
}
